// Auth context object + the useAuth hook that consumes it. Lives
// outside AuthContext.tsx so that file can export ONLY the
// AuthProvider component, which in turn lets Vite's React Fast Refresh
// work correctly when AuthProvider is edited.
//
// (See react-refresh/only-export-components: a file that exports both
// a component and a hook breaks HMR for the component.)

import { createContext, useContext } from 'react';
import type { User, Session } from '@supabase/supabase-js';
import type { Profile } from '../lib/types';

// Result returned by signUp. `needsConfirmation` is true when Supabase
// did NOT create a session (i.e., email confirmation is enabled and the
// user has to click the link in their inbox before logging in).
export interface SignUpResult {
  error: Error | null;
  needsConfirmation: boolean;
}

export interface AuthState {
  user: User | null;
  profile: Profile | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, displayName: string) => Promise<SignUpResult>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  // M29 — password recovery flow.
  // `resetPasswordForEmail` sends the recovery email with a link back to
  // /reset-password. `updatePassword` is called on that page after
  // Supabase has restored a recovery session from the URL.
  resetPasswordForEmail: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: Error | null }>;
  // M36 — self-serve account deletion. Calls the `delete_my_account()`
  // RPC which is SECURITY DEFINER + tied to auth.uid(), so the caller
  // can only delete themselves. Cascades through every user-owned
  // table via FK ON DELETE CASCADE.
  deleteAccount: () => Promise<{ error: Error | null }>;
}

export const AuthContext = createContext<AuthState | undefined>(undefined);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
